import { normalizeAttrCode, setAttributeValue } from "./Attributes";

import type { IConfiguratorState, TCamera, TFloorplan, TMaterialAttribute, TProduct, TProductPart, TShareableState } from "./Types";
  
export function productFromMastercodes(id: 0 | 1,
    masterCodes: string[] | string,
    extended: boolean): TProduct {
    const codes = Array.isArray(masterCodes) ? masterCodes : JSON.parse(masterCodes) as string[];
    const parts: TProductPart[] = codes.map((code) => {
        return {
            code_master: code,
            extended,
        };
    });
    return { id, parts };
}
  
export class Configurator implements IConfiguratorState {
    public floorplans: TFloorplan[] = [];
    public materialAttributes: TMaterialAttribute[] = [];
    public cameras: TCamera[] = [];
  
    public currentFloorplanId = "0";
    public primary: TProduct = { id: 0, parts: [] };
    public secondary: TProduct | null = null;
  
    constructor(init?: Partial<IConfiguratorState>) {
        Object.assign(this, init);
    }
  
    public get hasChairs(): boolean {
        const fp = this.floorplans.find((f) => f.nb_floorplan === this.currentFloorplanId);
        return !!fp && fp.nb_pivot_points > 1;
    }
  
    public setFloorplan(id: string) {
        console.log("Setting floorplan to", id);
        this.currentFloorplanId = id;
        if (!this.hasChairs) {
            this.secondary = null;
        }
        else if (!this.secondary) {
        // initialize empty chair template
            this.secondary = { id: 1, parts: [] };
        }
    }
  
    public setPrimaryFromMastercodes(masterCodes: string[] | string, extended: boolean) {
        this.primary = productFromMastercodes(0, masterCodes, extended);
    }
  
    public setChairTemplateFromMastercodes(masterCodes: string[] | string, extended: boolean) {
        if (!this.hasChairs) { return; }
        this.secondary = productFromMastercodes(1, masterCodes, extended);
    }
  
    public setExtended(productId: 0 | 1, extended: boolean) {
        const sel = productId === 0 ? this.primary : this.secondary;
        if (!sel) { return; }
        sel.parts.forEach((p) => p.extended = extended);
    }
  
    public setAttribute(productId: 0 | 1, partCodeMaster: string, code: string, occurrenceIndex: number, value: string) {
        const sel = productId === 0 ? this.primary : this.secondary;
        if (!sel) { return; }
        const part = sel.parts.find((p) => p.mastercode === partCodeMaster);
        if (!part) { return; }
        setAttributeValue(part, normalizeAttrCode(code), occurrenceIndex, value);
    }
  
    public toSceneJson(): any[] {
        const payload: any[] = [
            { type: "floorplan", nb_floorplan: this.currentFloorplanId },
        ];
  
        // primary at position 0
        payload.push(serializeProduct(this.primary, 0));
  
        // replicate chairs around the table based on pivot points
        const fp = this.floorplans.find((f) => f.nb_floorplan === this.currentFloorplanId);
        const count = Math.max((fp?.nb_pivot_points ?? 1) - 1, 0);
        if (this.hasChairs && this.secondary) {
            for (let i = 0; i < count; i++) {
                payload.push(serializeProduct(this.secondary, i + 1, /*forceId*/ 1));
            }
        }
  
        return payload;
    }
}
  
/** INTERNALS */
  
function serializeProduct(sel: TProduct, position: number, forceId?: 0 | 1) {
    return {
        id: forceId ?? sel.id,
        type: "object",
        position,
        parts: sel.parts,
    };
}
